import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { View, Text, TouchableOpacity, TextInput, ScrollView, ActivityIndicator, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Constants from 'expo-constants';

const getBase = () => (
  (typeof process !== 'undefined' && process?.env?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
  || Constants?.expoConfig?.extra?.EXPO_PUBLIC_FUNCTIONS_BASE_URL
  || Constants?.expoConfig?.extra?.FUNCTIONS_BASE_URL
  || Constants?.manifest?.extra?.FUNCTIONS_BASE_URL
);

const DIRECTIONS = ['over', 'under'];

export default function NoResultsFeedback({ queryResolution, originalQuery, message }) {
  const [expanded, setExpanded] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dropdownData, setDropdownData] = useState(null);
  const [openField, setOpenField] = useState(null);
  const [searchQ, setSearchQ] = useState('');

  const entities = queryResolution?.entities || {};
  const [form, setForm] = useState({
    sport: entities.league ? guessSport(entities.league) : '',
    league: entities.league || '',
    team: entities.team || '',
    player: entities.player || '',
    market: entities.market || '',
    line: entities.line ? String(entities.line) : '',
    direction: entities.direction || '',
    comment: ''
  });

  useEffect(() => {
    if (expanded && !dropdownData) {
      (async () => {
        try {
          const r = await fetch(`${getBase()}/entityResolutions?action=getDropdownData`);
          const j = await r.json();
          if (j.success) setDropdownData(j);
        } catch (_) {}
      })();
    }
  }, [expanded, dropdownData]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const r = await fetch(`${getBase()}/entityResolutions`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'userFeedback', originalQuery, correction: form })
      });
      const j = await r.json();
      if (j.success) setSubmitted(true);
    } catch (_) {}
    finally { setSubmitting(false); }
  };

  const sportsFromData = dropdownData?.sports || [];
  const allLeagues = dropdownData?.leagues || [];
  const leaguesForSport = useMemo(() => {
    if (!form.sport || !dropdownData) return allLeagues;
    const all = [...(dropdownData.teams || []), ...(dropdownData.players || [])];
    const sportLeagues = new Set(all.filter(i => i.sport?.toLowerCase() === form.sport.toLowerCase()).map(i => i.league).filter(Boolean));
    const sportFirst = [...sportLeagues].sort();
    const rest = allLeagues.filter(l => !sportLeagues.has(l));
    return sportFirst.length > 0 ? [...sportFirst, ...rest] : allLeagues;
  }, [dropdownData, form.sport, allLeagues]);

  const teamsPool = useMemo(() => {
    if (!dropdownData?.teams) return [];
    let items = dropdownData.teams;
    if (form.sport) items = items.filter(t => t.sport?.toLowerCase() === form.sport.toLowerCase());
    if (form.league) items = items.filter(t => t.league?.toLowerCase() === form.league.toLowerCase());
    return items;
  }, [dropdownData, form.sport, form.league]);

  const playersPool = useMemo(() => {
    if (!dropdownData?.players) return [];
    let items = dropdownData.players;
    if (form.sport) items = items.filter(p => p.sport?.toLowerCase() === form.sport.toLowerCase());
    if (form.league) items = items.filter(p => p.league?.toLowerCase() === form.league.toLowerCase());
    if (form.team) {
      const teamFiltered = items.filter(p => p.team?.toLowerCase() === form.team.toLowerCase());
      if (teamFiltered.length > 0) {
        const rest = items.filter(p => p.team?.toLowerCase() !== form.team.toLowerCase());
        return [...teamFiltered, ...rest];
      }
    }
    return items;
  }, [dropdownData, form.sport, form.league, form.team]);

  const marketsPool = useMemo(() => dropdownData?.markets || [], [dropdownData]);

  const filterItems = useCallback((query, pool) => {
    if (!query || query.length < 1 || !pool?.length) return pool.slice(0, 20);
    const q = query.toLowerCase();
    return pool.filter(i => (i.name || i).toLowerCase().includes(q))
      .sort((a, b) => {
        const an = (a.name || a).toLowerCase();
        const bn = (b.name || b).toLowerCase();
        return (an.startsWith(q) ? 0 : 1) - (bn.startsWith(q) ? 0 : 1) || an.localeCompare(bn);
      }).slice(0, 20);
  }, []);

  // Interpretation display
  const interpreted = [
    entities.player && ['Player', entities.player, '#c084fc'],
    entities.team && ['Team', entities.team, '#60a5fa'],
    entities.market && ['Market', entities.market, '#f59e0b'],
    entities.league && ['League', entities.league?.toUpperCase(), '#6b7280'],
    entities.line && ['Line', `${entities.direction || ''} ${entities.line}`.trim(), '#10b981'],
    entities.sportsbook && ['Book', entities.sportsbook, '#6b7280']
  ].filter(Boolean);

  if (submitted) {
    return (
      <View style={st.wrap}>
        <View style={st.successBox}>
          <Ionicons name="checkmark-circle" size={20} color="#10b981" />
          <Text style={st.successTxt}>Thanks! Your feedback has been recorded and will help improve results.</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={st.wrap}>
      {/* System interpretation */}
      <View style={st.interpBox}>
        <View style={st.interpHeader}>
          <Ionicons name="search-outline" size={16} color="rgba(255,255,255,0.4)" />
          <Text style={st.interpTitle}>We interpreted your search as:</Text>
        </View>
        {interpreted.length > 0 ? (
          <View style={st.tagRow}>
            {interpreted.map(([label, value, color], i) => (
              <View key={i} style={st.tag}>
                <Text style={st.tagKey}>{label}</Text>
                <Text style={[st.tagVal, { color }]}>{value}</Text>
              </View>
            ))}
          </View>
        ) : (
          <Text style={st.interpEmpty}>Could not determine what you were looking for</Text>
        )}
        {message && <Text style={st.msgTxt}>{message}</Text>}
      </View>

      {/* Feedback toggle */}
      {!expanded && (
        <TouchableOpacity style={st.feedbackBtn} onPress={() => setExpanded(true)}>
          <Ionicons name="create-outline" size={15} color="#fff" />
          <Text style={st.feedbackBtnTxt}>Not what you meant? Tell us what you were looking for</Text>
        </TouchableOpacity>
      )}

      {/* Correction form */}
      {expanded && (
        <View style={st.formBox}>
          <Text style={st.formTitle}>What were you actually looking for?</Text>
          <Text style={st.formSub}>Fill in as many fields as you can — this helps our system learn</Text>

          {/* Sport */}
          <View style={st.field}>
            <Text style={st.fLabel}>Sport</Text>
            <View style={st.chipRow}>
              {sportsFromData.map(s => (
                <TouchableOpacity key={s} style={[st.chipSm, form.sport === s && st.chipOn]}
                  onPress={() => setForm(p => ({ ...p, sport: p.sport === s ? '' : s, league: '', team: '', player: '' }))}>
                  <Text style={[st.chipSmTxt, form.sport === s && st.chipTxtOn]}>{s}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* League */}
          <View style={st.field}>
            <Text style={st.fLabel}>League</Text>
            <View style={st.chipRow}>
              {leaguesForSport.map(l => (
                <TouchableOpacity key={l} style={[st.chipSm, form.league === l && st.chipOn]}
                  onPress={() => setForm(p => ({ ...p, league: p.league === l ? '' : l, team: '', player: '' }))}>
                  <Text style={[st.chipSmTxt, form.league === l && st.chipTxtOn]}>{l}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Team */}
          <SField label="Team" value={form.team} placeholder="Search teams..."
            isOpen={openField === 'team'} query={searchQ}
            onOpen={() => { setOpenField('team'); setSearchQ(form.team || ''); }}
            onClose={() => setOpenField(null)} onSearch={setSearchQ}
            items={filterItems(searchQ, teamsPool)}
            onSelect={(item) => { setForm(p => ({ ...p, team: item.name, sport: item.sport || p.sport, league: item.league || p.league })); setOpenField(null); }}
            renderItem={(item) => <><Text style={st.ddName}>{item.name}</Text><Text style={st.ddMeta}>{item.league?.toUpperCase()}</Text></>}
            onClear={() => setForm(p => ({ ...p, team: '' }))} />

          {/* Player */}
          <SField label="Player" value={form.player} placeholder="Search players..."
            isOpen={openField === 'player'} query={searchQ}
            onOpen={() => { setOpenField('player'); setSearchQ(form.player || ''); }}
            onClose={() => setOpenField(null)} onSearch={setSearchQ}
            items={filterItems(searchQ, playersPool)}
            onSelect={(item) => { setForm(p => ({ ...p, player: item.name, team: item.team || p.team, sport: item.sport || p.sport, league: item.league || p.league })); setOpenField(null); }}
            renderItem={(item) => <><Text style={st.ddName}>{item.name}</Text><Text style={st.ddMeta}>{item.team} · {item.league?.toUpperCase()}</Text></>}
            onClear={() => setForm(p => ({ ...p, player: '' }))} />

          {/* Market */}
          <SField label="Market" value={form.market} placeholder="Search markets..."
            isOpen={openField === 'market'} query={searchQ}
            onOpen={() => { setOpenField('market'); setSearchQ(form.market || ''); }}
            onClose={() => setOpenField(null)} onSearch={setSearchQ}
            items={filterItems(searchQ, marketsPool)}
            onSelect={(item) => { setForm(p => ({ ...p, market: item.name || item })); setOpenField(null); }}
            renderItem={(item) => <><Text style={st.ddName}>{item.name || item}</Text>{item.key && <Text style={st.ddMeta}>{item.key}</Text>}</>}
            onClear={() => setForm(p => ({ ...p, market: '' }))} />

          {/* Line + Direction */}
          <View style={{ flexDirection: 'row', gap: 10 }}>
            <View style={[st.field, { flex: 1 }]}>
              <Text style={st.fLabel}>Line</Text>
              <TextInput style={st.fInput} value={form.line} onChangeText={v => setForm(p => ({ ...p, line: v }))}
                placeholder="e.g. 14, -3.5" placeholderTextColor="rgba(255,255,255,0.25)" keyboardType="numeric" />
            </View>
            <View style={[st.field, { flex: 1 }]}>
              <Text style={st.fLabel}>Direction</Text>
              <View style={st.chipRow}>
                {DIRECTIONS.map(d => (
                  <TouchableOpacity key={d} style={[st.chipSm, form.direction === d && st.chipOn, { flex: 1, alignItems: 'center' }]}
                    onPress={() => setForm(p => ({ ...p, direction: p.direction === d ? '' : d }))}>
                    <Text style={[st.chipSmTxt, form.direction === d && st.chipTxtOn]}>{d.charAt(0).toUpperCase() + d.slice(1)}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          </View>

          {/* Comment */}
          <View style={st.field}>
            <Text style={st.fLabel}>Anything else? (optional)</Text>
            <TextInput style={[st.fInput, { height: 60 }]} value={form.comment} onChangeText={v => setForm(p => ({ ...p, comment: v }))}
              placeholder="e.g. I was looking for Trae Young rebounds over 6.5" placeholderTextColor="rgba(255,255,255,0.2)" multiline />
          </View>

          {/* Preview */}
          <CorrectionPreview form={form} originalQuery={originalQuery} />

          <View style={{ flexDirection: 'row', gap: 8, marginTop: 6 }}>
            <TouchableOpacity style={st.submitBtn} onPress={handleSubmit} disabled={submitting}>
              {submitting ? <ActivityIndicator size="small" color="#fff" /> :
                <><Ionicons name="send" size={14} color="#fff" /><Text style={st.submitTxt}>Submit Feedback</Text></>}
            </TouchableOpacity>
            <TouchableOpacity style={st.cancelBtn} onPress={() => setExpanded(false)}>
              <Text style={st.cancelTxt}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}
    </View>
  );
}

function CorrectionPreview({ form, originalQuery }) {
  const parts = [
    form.sport && ['Sport', form.sport, '#6b7280'],
    form.league && ['League', form.league?.toUpperCase(), '#6b7280'],
    form.team && ['Team', form.team, '#60a5fa'],
    form.player && ['Player', form.player, '#c084fc'],
    form.market && ['Market', form.market, '#f59e0b'],
    form.line && ['Line', form.line, '#fff'],
    form.direction && ['Dir', form.direction?.charAt(0).toUpperCase() + form.direction?.slice(1), form.direction === 'over' ? '#10b981' : '#ef4444']
  ].filter(Boolean);
  if (parts.length === 0) return null;
  return (
    <View style={st.previewBox}>
      <Text style={st.previewTitle}>Your correction</Text>
      <Text style={st.previewInput}>"{originalQuery}"  =</Text>
      <View style={st.tagRow}>
        {parts.map(([label, value, color], i) => (
          <View key={i} style={st.tag}>
            <Text style={st.tagKey}>{label}</Text>
            <Text style={[st.tagVal, { color }]}>{value}</Text>
          </View>
        ))}
      </View>
    </View>
  );
}

function SField({ label, value, placeholder, isOpen, query, onOpen, onClose, onSearch, items, onSelect, renderItem, onClear }) {
  const inputRef = React.useRef(null);
  return (
    <View style={[st.field, isOpen && { zIndex: 50 }]}>
      <Text style={st.fLabel}>{label}</Text>
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        <TextInput ref={inputRef} style={[st.fInput, { flex: 1 }, isOpen && { borderColor: 'rgba(59,130,246,0.4)' }]}
          value={isOpen ? query : value} placeholder={placeholder}
          placeholderTextColor="rgba(255,255,255,0.25)" onFocus={onOpen}
          onChangeText={v => { if (!isOpen) onOpen(); onSearch(v); }} />
        {value ? <TouchableOpacity style={{ position: 'absolute', right: 8 }} onPress={() => { onClear(); onClose(); }}>
          <Ionicons name="close-circle" size={16} color="rgba(255,255,255,0.3)" /></TouchableOpacity> : null}
      </View>
      {isOpen && (
        <>
          {Platform.OS === 'web' && (
            <TouchableOpacity style={st.ddBackdrop} activeOpacity={1} onPress={() => { inputRef.current?.blur(); onClose(); }} />
          )}
          {items?.length > 0 && (
            <View style={st.dropdown}>
              <ScrollView style={{ maxHeight: 160 }} nestedScrollEnabled keyboardShouldPersistTaps="handled">
                {items.map((item, i) => (
                  <TouchableOpacity key={`${item.name || item}:${i}`} style={st.ddItem} onPress={() => onSelect(item)}>
                    {renderItem(item)}
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>
          )}
        </>
      )}
    </View>
  );
}

function guessSport(league) {
  if (!league) return '';
  const l = league.toLowerCase();
  if (['nba', 'wnba', 'ncaab'].includes(l)) return 'basketball';
  if (['nfl', 'ncaaf'].includes(l)) return 'football';
  if (l === 'nhl') return 'hockey';
  if (l === 'mlb') return 'baseball';
  if (['atp', 'wta'].includes(l)) return 'tennis';
  if (['ufc', 'pfl', 'bellator'].includes(l)) return 'mma';
  if (l.includes('premier') || l.includes('liga') || l.includes('mls')) return 'soccer';
  return '';
}

const st = {
  wrap: { marginTop: 12 },
  interpBox: { padding: 12, borderRadius: 10, backgroundColor: 'rgba(255,255,255,0.04)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.06)', marginBottom: 10 },
  interpHeader: { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 6 },
  interpTitle: { color: 'rgba(255,255,255,0.5)', fontSize: 12, fontWeight: '600' },
  interpEmpty: { color: 'rgba(255,255,255,0.25)', fontSize: 12, fontStyle: 'italic' },
  msgTxt: { color: 'rgba(255,255,255,0.4)', fontSize: 12, marginTop: 6 },
  tagRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  tag: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingVertical: 3, paddingHorizontal: 8, borderRadius: 6, backgroundColor: 'rgba(255,255,255,0.05)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.06)' },
  tagKey: { color: 'rgba(255,255,255,0.35)', fontSize: 10, fontWeight: '600' },
  tagVal: { color: '#fff', fontSize: 11, fontWeight: '700' },
  feedbackBtn: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: 10, paddingHorizontal: 14, borderRadius: 8, backgroundColor: 'rgba(59,130,246,0.1)', borderWidth: 1, borderColor: 'rgba(59,130,246,0.2)' },
  feedbackBtnTxt: { color: '#60a5fa', fontSize: 13, fontWeight: '600' },
  formBox: { padding: 14, borderRadius: 10, backgroundColor: 'rgba(255,255,255,0.03)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.06)' },
  formTitle: { color: '#fff', fontSize: 15, fontWeight: '700', marginBottom: 2 },
  formSub: { color: 'rgba(255,255,255,0.35)', fontSize: 11, marginBottom: 12 },
  field: { marginBottom: 10 },
  fLabel: { color: 'rgba(255,255,255,0.45)', fontSize: 11, fontWeight: '600', marginBottom: 3 },
  fInput: { height: 36, paddingHorizontal: 11, borderRadius: 7, borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)', color: '#fff', fontSize: 13 },
  chipRow: { flexDirection: 'row', gap: 4, flexWrap: 'wrap' },
  chipSm: { paddingVertical: 3, paddingHorizontal: 7, borderRadius: 8, borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)' },
  chipOn: { backgroundColor: 'rgba(255,255,255,0.14)', borderColor: 'rgba(255,255,255,0.25)' },
  chipSmTxt: { color: 'rgba(255,255,255,0.45)', fontWeight: '600', fontSize: 10 },
  chipTxtOn: { color: '#fff' },
  ddBackdrop: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 90 },
  dropdown: { borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)', borderRadius: 8, backgroundColor: 'rgba(18,18,18,0.98)', marginTop: 3, ...(Platform.OS === 'web' ? { position: 'relative', zIndex: 100 } : {}) },
  ddItem: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 7, paddingHorizontal: 11, borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.03)' },
  ddName: { color: '#fff', fontSize: 12, fontWeight: '500', flex: 1 },
  ddMeta: { color: 'rgba(255,255,255,0.3)', fontSize: 10, marginLeft: 8 },
  previewBox: { padding: 10, borderRadius: 8, backgroundColor: 'rgba(16,185,129,0.04)', borderWidth: 1, borderColor: 'rgba(16,185,129,0.12)', marginBottom: 6 },
  previewTitle: { color: 'rgba(255,255,255,0.4)', fontSize: 10, fontWeight: '700', letterSpacing: 0.5, marginBottom: 4 },
  previewInput: { color: '#f59e0b', fontSize: 12, fontWeight: '600', marginBottom: 6 },
  submitBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingVertical: 9, paddingHorizontal: 16, borderRadius: 8, backgroundColor: '#3b82f6' },
  submitTxt: { color: '#fff', fontWeight: '700', fontSize: 12 },
  cancelBtn: { paddingVertical: 9, paddingHorizontal: 16, borderRadius: 8, backgroundColor: 'rgba(255,255,255,0.06)' },
  cancelTxt: { color: 'rgba(255,255,255,0.5)', fontWeight: '600', fontSize: 12 },
  successBox: { flexDirection: 'row', alignItems: 'center', gap: 8, padding: 12, borderRadius: 8, backgroundColor: 'rgba(16,185,129,0.06)', borderWidth: 1, borderColor: 'rgba(16,185,129,0.15)' },
  successTxt: { color: '#10b981', fontSize: 13, fontWeight: '600', flex: 1 },
};
